import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import {
	AppError,
	AUTH_MESSAGE_CODES,
	CORE_ROUTE,
	CustomerManagementAction,
	LoginWithVerificationCodeRequest,
	MessageScopeType,
	MessageService,
	MessageType,
	SESSION_ACTION_TYPE,
	SessionAction,
} from "@obg/ngx.core";
import { PlatformService, WindowRef } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { fromEvent, merge, of } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";

import { SUSPICIOUS_LOGIN_ACTION_TYPE, SuspiciousLoginAction } from "./suspicious-login.action";
import { SuspiciousLoginConfigService } from "./suspicious-login.config";
import { SUSPICIOUS_LOGIN_EXTERNAL_EVENT_NAME, SUSPICIOUS_LOGIN_MESSAGE_CODES } from "./suspicious-login.const";
import { NativeSuspiciousLogin, SuspiciousLoginPinRequest, SuspiciousLoginResponse } from "./suspicious-login.model";
import { SuspiciousLoginService } from "./suspicious-login.service";

@Injectable()
export class SuspiciousLoginEffect {
	loginFail$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginFail),
			map((x: ActionWithPayload<AppError>) => x.payload),
			filter((x) => x.code === AUTH_MESSAGE_CODES.login.customerNotAllowed),
			map((x) => x.data as SuspiciousLoginResponse),
			map((response) => this.suspiciousLoginAction.setCommunicationChannels(response.challengeDetails))
		)
	);

	requestPin$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SUSPICIOUS_LOGIN_ACTION_TYPE.requestPin),
			map((x: ActionWithPayload<SuspiciousLoginPinRequest>) => x.payload),
			switchMap((details) =>
				this.service.requestPin(details).pipe(
					map(() => this.suspiciousLoginAction.requestPinSuccess()),
					catchError(() => of(this.suspiciousLoginAction.requestPinFail()))
				)
			)
		)
	);

	verifyPin$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SUSPICIOUS_LOGIN_ACTION_TYPE.verifyPin),
			map((x: ActionWithPayload<LoginWithVerificationCodeRequest>) => x.payload),
			map((request) => this.sessionAction.loginWithVerificationCode(request))
		)
	);

	loginWithVerificationCodeSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess),
			map(() => this.suspiciousLoginAction.clearCommunicationChannels())
		)
	);

	externalSuspiciousLogin$ = this.platformService.forClient(() =>
		createEffect(() =>
			fromEvent<CustomEvent>(this.windowRef.native.document, SUSPICIOUS_LOGIN_EXTERNAL_EVENT_NAME).pipe(
				filter(() => this.config.getExternalEventEnabled()),
				map((event) => event.detail as NativeSuspiciousLogin),
				map((response) =>
					batchActions(
						this.suspiciousLoginAction.setCommunicationChannels(response.loginResponse.challengeDetails),
						this.customerManagementAction.updateCredentials(response.loginRequest),
						RouteAction.navigate(CORE_ROUTE.auth.suspiciousLogin)
					)
				)
			)
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.map$({
				[SUSPICIOUS_LOGIN_ACTION_TYPE.requestPinFail]: {
					context: SUSPICIOUS_LOGIN_ACTION_TYPE.requestPin,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: SUSPICIOUS_LOGIN_MESSAGE_CODES.error.requestPinFailed,
				},
				[SUSPICIOUS_LOGIN_ACTION_TYPE.requestPinSuccess]: {
					context: SUSPICIOUS_LOGIN_ACTION_TYPE.requestPin,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: SUSPICIOUS_LOGIN_MESSAGE_CODES.success.requestPinSuccess,
				},
			}),
			this.messageService.mapClearByContext$({
				[SUSPICIOUS_LOGIN_ACTION_TYPE.requestPin]: SUSPICIOUS_LOGIN_ACTION_TYPE.requestPin,
			})
		)
	);

	constructor(
		private actions$: Actions,
		private suspiciousLoginAction: SuspiciousLoginAction,
		private sessionAction: SessionAction,
		private service: SuspiciousLoginService,
		private config: SuspiciousLoginConfigService,
		private customerManagementAction: CustomerManagementAction,
		private windowRef: WindowRef,
		private messageService: MessageService,
		private platformService: PlatformService
	) {}
}
